/**
 * Application Configuration — single source of truth.
 * Reads from Vite env via import.meta.env.
 * @see docs/ENVIRONMENT.md for required env vars and .env template.
 */

const env = typeof import.meta !== 'undefined' ? import.meta.env : ({} as ImportMetaEnv);

const REQUIRED_ENV_VARS = ['VITE_API_BASE_URL'] as const;

function validateRequiredEnv(): void {
  const missing = REQUIRED_ENV_VARS.filter((key) => {
    const value = env[key];
    return value === undefined || value === '';
  });
  if (missing.length > 0) {
    throw new Error(
      `Missing required environment variable(s): ${missing.join(', ')}. See docs/ENVIRONMENT.md for setup.`
    );
  }
}

validateRequiredEnv();

// Application Settings
// APP_NAME: fallback for non-React code (e.g. scripts). In UI, prefer useClientTheme().appTitle for multi-client branding.
export const APP_CONFIG = {
  DEFAULT_LANGUAGE: 'ar',
  SUPPORTED_LANGUAGES: ['ar', 'en'],
  NOTIFICATION_DURATION: 4000,
  APP_NAME: 'ID Channel Dashboard',
  VERSION: '1.0.0'
} as const;

// Pagination Configuration
// pageSize === ALL_PAGE_SIZE (0) means "show all rows" — sent to the API as
// limit=0, which the backend resolveLimit maps to the full (capped) set.
export const ALL_PAGE_SIZE = 0;

export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [20, 50, 100, ALL_PAGE_SIZE],
  MAX_PAGE_SIZE: 100,
  MIN_PAGE_SIZE: 20,
  DEFAULT_PAGE: 1
} as const;

// Performance Configuration
export const PERFORMANCE_CONFIG = {
  SEARCH_DEBOUNCE_DELAY: 300,
  INPUT_DEBOUNCE_DELAY: 300,
  API_DEBOUNCE_DELAY: 300,
  API_THROTTLE_LIMIT: 100,
  UI_THROTTLE_LIMIT: 100,
  SCROLL_THROTTLE_LIMIT: 100,
  API_TIMEOUT: 30000,
  CONNECTION_TIMEOUT: 10000,
  REQUEST_TIMEOUT: 15000,
  CACHE_DURATION: 300000,
  STALE_TIME: 60000,
  REFETCH_INTERVAL: 300000
} as const;

// UI Configuration
export const UI_CONFIG = {
  ANIMATION_DURATION: 300,
  TRANSITION_DURATION: 200,
  LOADING_DELAY: 100,
  MODAL_ANIMATION_DURATION: 200,
  MODAL_BACKDROP_OPACITY: 0.5,
  TABLE_ROW_HEIGHT: 48,
  TABLE_HEADER_HEIGHT: 56,
  TABLE_PAGINATION_HEIGHT: 64,
  FORM_VALIDATION_DELAY: 300,
  FORM_AUTO_SAVE_DELAY: 2000,
  NOTIFICATION_DURATION: 4000,
  SUCCESS_NOTIFICATION_DURATION: 3000,
  ERROR_NOTIFICATION_DURATION: 5000,
  WARNING_NOTIFICATION_DURATION: 4000
} as const;

// API Configuration (reads from Vite env; no fallback — validateRequiredEnv ensures VITE_API_BASE_URL is set)
export const API_CONFIG = {
  BASE_URL: env.VITE_API_BASE_URL,
  API_VERSION: 'v1',
  DEFAULT_TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
  RATE_LIMIT_REQUESTS: 100,
  RATE_LIMIT_WINDOW: 60000,
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  CACHE_DURATION: 300000,
  STALE_TIME: 60000
} as const;

// Feature Flags
export const FEATURE_FLAGS = {
  ENABLE_PERFORMANCE_MONITORING: true,
  ENABLE_ERROR_BOUNDARIES: true,
  ENABLE_LAZY_LOADING: true,
  ENABLE_VIRTUAL_SCROLLING: false,
  ENABLE_OFFLINE_MODE: false,
  ENABLE_REAL_TIME_UPDATES: true
} as const;

// Validation Configuration
export const VALIDATION_CONFIG = {
  MIN_PASSWORD_LENGTH: 8,
  MAX_PASSWORD_LENGTH: 128,
  PASSWORD_REQUIREMENTS: {
    MIN_LENGTH: 8,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBERS: true,
    REQUIRE_SPECIAL_CHARS: true
  },
  MIN_NAME_LENGTH: 2,
  MAX_NAME_LENGTH: 100,
  MIN_DESCRIPTION_LENGTH: 10,
  MAX_DESCRIPTION_LENGTH: 500,
  PHONE_NUMBER_PATTERN: /^[\+]?[1-9][\d]{0,15}$/,
  PHONE_NUMBER_MIN_LENGTH: 10,
  PHONE_NUMBER_MAX_LENGTH: 15,
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  MAX_FILE_SIZE: 5 * 1024 * 1024,
  ALLOWED_FILE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
  MAX_FILES_PER_UPLOAD: 10
} as const;

// Business Logic Configuration
export const BUSINESS_CONFIG = {
  MAX_STORES_PER_USER: 50,
  MIN_STORE_NAME_LENGTH: 3,
  MAX_STORE_NAME_LENGTH: 100,
  MAX_PRODUCTS_PER_STORE: 1000,
  MIN_PRODUCT_NAME_LENGTH: 3,
  MAX_PRODUCT_NAME_LENGTH: 200,
  MAX_PRODUCT_DESCRIPTION_LENGTH: 1000,
  MIN_STOCK_QUANTITY: 0,
  MAX_STOCK_QUANTITY: 999999,
  LOW_STOCK_THRESHOLD: 10,
  CRITICAL_STOCK_THRESHOLD: 5,
  MAX_USERS_PER_ROLE: 100,
  MIN_USERNAME_LENGTH: 3,
  MAX_USERNAME_LENGTH: 50,
  MAX_DISTRIBUTION_RECORDS: 10000,
  MIN_DISTRIBUTION_QUANTITY: 1,
  MAX_DISTRIBUTION_QUANTITY: 9999
} as const;

// Security Configuration
export const SECURITY_CONFIG = {
  SESSION_TIMEOUT: 30 * 60 * 1000,
  REFRESH_TOKEN_DURATION: 7 * 24 * 60 * 60 * 1000,
  PASSWORD_EXPIRY_DAYS: 90,
  MAX_LOGIN_ATTEMPTS: 5,
  LOCKOUT_DURATION: 15 * 60 * 1000,
  CSRF_TOKEN_LENGTH: 32,
  CSRF_TOKEN_EXPIRY: 60 * 60 * 1000,
  LOGIN_RATE_LIMIT: 5,
  API_RATE_LIMIT: 100
} as const;

// Development Configuration (Vite: import.meta.env.MODE / import.meta.env.DEV)
const isDev = typeof import.meta !== 'undefined' && (import.meta.env?.MODE === 'development' || import.meta.env?.DEV === true);

export const DEV_CONFIG = {
  ENABLE_DEBUG_LOGGING: isDev,
  ENABLE_PERFORMANCE_PROFILING: isDev,
  ENABLE_REDUX_DEVTOOLS: isDev,
  ENABLE_HOT_RELOAD: isDev,
  LOG_LEVEL: isDev ? 'debug' : 'error',
  ENABLE_CONSOLE_LOGGING: isDev,
  ENABLE_NETWORK_LOGGING: isDev
} as const;

// Single object for easy access
export const CONFIG = {
  APP: APP_CONFIG,
  PAGINATION: PAGINATION_CONFIG,
  PERFORMANCE: PERFORMANCE_CONFIG,
  UI: UI_CONFIG,
  API: API_CONFIG,
  FEATURES: FEATURE_FLAGS,
  VALIDATION: VALIDATION_CONFIG,
  BUSINESS: BUSINESS_CONFIG,
  SECURITY: SECURITY_CONFIG,
  DEV: DEV_CONFIG
} as const;

export type AppConfig = typeof APP_CONFIG;
export type PaginationConfig = typeof PAGINATION_CONFIG;
export type PerformanceConfig = typeof PERFORMANCE_CONFIG;
export type UIConfig = typeof UI_CONFIG;
export type APIConfig = typeof API_CONFIG;
export type FeatureFlags = typeof FEATURE_FLAGS;
export type ValidationConfig = typeof VALIDATION_CONFIG;
export type BusinessConfig = typeof BUSINESS_CONFIG;
export type SecurityConfig = typeof SECURITY_CONFIG;
export type DevConfig = typeof DEV_CONFIG;
export type AppConfiguration = typeof CONFIG;
